import { useEffect, useRef, useState } from 'react';
import {
    IComparisonBoardFilters,
    IComparisonBoardItem,
    ICreateComparisonBoardRequest,
} from '../../../interfaces/comparison-boards/IComparisonBoards';
import {
    useCreateComparisonBoardMutation,
    useGetComparisonBoardsMutation,
    useUpdateComparisonBoardStatusMutation,
} from '../../../store/chatbox/chatboxApi';
import {
    useGetComparisonBoardClientsQuery,
    useGetComparisonBoardPropertiesQuery,
} from '../../../store/users/usersApi';
import {
    resolveBillyHasMore,
    resolveBillyPaginatedData,
} from '../../../utils/pagination/billyPagination';
import ComparisonBoardFilters from './components/ComparisonBoardFilters';
import ComparisonBoardsTable from './components/ComparisonBoardsTable';
import CreateComparisonBoardModal from './components/CreateComparisonBoardModal';
import {
    getComparisonBoardActiveClientId,
} from './utils';

const LeoComparisonBoards = () => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const requestNextPageRef = useRef(false);
    const filtersInitializedRef = useRef(false);

    const [paginationState, setPaginationState] = useState({
        pageNumber: 1,
        pageSize: 10,
    });
    const [filters, setFilters] = useState<IComparisonBoardFilters>({});
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [appliedFilters, setAppliedFilters] = useState<IComparisonBoardFilters>({});
    const [rows, setRows] = useState<IComparisonBoardItem[]>([]);
    const [pageData, setPageData] = useState<any>(undefined);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [listRefreshKey, setListRefreshKey] = useState(0);
    const [updatingIds, setUpdatingIds] = useState<string[]>([]);

    const [getComparisonBoards] = useGetComparisonBoardsMutation();
    const [createComparisonBoard, { isLoading: creatingBoard }] = useCreateComparisonBoardMutation();
    const [updateComparisonBoardStatus] = useUpdateComparisonBoardStatusMutation();
    const {
        data: clients = [],
        isFetching: loadingClients,
    } = useGetComparisonBoardClientsQuery();
    const {
        data: properties = [],
        isFetching: loadingProperties,
    } = useGetComparisonBoardPropertiesQuery();

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(filters.search?.trim() || '');
        }, 400);

        return () => clearTimeout(timer);
    }, [filters.search]);

    useEffect(() => {
        if (!filtersInitializedRef.current) {
            filtersInitializedRef.current = true;
            return;
        }

        setRows([]);
        setPageData(undefined);
        setHasMore(true);
        requestNextPageRef.current = false;
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
        setAppliedFilters({
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(typeof filters.active === 'boolean' ? { active: filters.active } : {}),
        });
        setPaginationState((prev) => ({
            ...prev,
            pageNumber: 1,
        }));
    }, [debouncedSearch, filters.active]);

    useEffect(() => {
        const fetchData = async () => {
            const isFirstPage = paginationState.pageNumber === 1;

            try {
                if (isFirstPage) {
                    setLoading(true);
                } else {
                    setLoadingMore(true);
                }

                const result = await getComparisonBoards({
                    pageNumber: paginationState.pageNumber,
                    pageSize: paginationState.pageSize,
                    data: appliedFilters,
                }).unwrap();

                const { elements, page } = resolveBillyPaginatedData<IComparisonBoardItem>(result);

                setPageData(page);
                setRows((prevRows) => (isFirstPage ? elements : [...prevRows, ...elements]));
                setHasMore(resolveBillyHasMore(page, elements.length, paginationState.pageSize || 10));
            } catch (error) {
                console.error(error);
                if (isFirstPage) {
                    setRows([]);
                    setPageData(undefined);
                }
                setHasMore(false);
            } finally {
                requestNextPageRef.current = false;
                setLoading(false);
                setLoadingMore(false);
            }
        };

        fetchData();
    }, [
        getComparisonBoards,
        paginationState.pageNumber,
        paginationState.pageSize,
        appliedFilters,
        listRefreshKey,
    ]);

    const refreshFirstPage = () => {
        setRows([]);
        setPageData(undefined);
        setHasMore(true);
        requestNextPageRef.current = false;
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
        setPaginationState((prev) => ({
            ...prev,
            pageNumber: 1,
        }));
        setListRefreshKey((prev) => prev + 1);
    };

    const onClearFilters = () => {
        const emptyFilters: IComparisonBoardFilters = {};
        setFilters(emptyFilters);
        setDebouncedSearch('');
    };

    const onLoadMore = () => {
        if (!hasMore || loadingMore || loading || requestNextPageRef.current) return;
        requestNextPageRef.current = true;
        setPaginationState((prev) => ({
            ...prev,
            pageNumber: (prev.pageNumber || 1) + 1,
        }));
    };

    const onCreateBoard = async (payload: ICreateComparisonBoardRequest) => {
        setCreateError(null);
        try {
            await createComparisonBoard(payload).unwrap();
            setCreateModalOpen(false);
            refreshFirstPage();
            return true;
        } catch (error: any) {
            setCreateError(
                error?.data?.message
                || error?.data?.error
                || error?.error
                || 'Unable to create comparison board. Please try again.',
            );
            return false;
        }
    };

    const onChangeStatus = async (row: IComparisonBoardItem, enabled: boolean) => {
        const id = getComparisonBoardActiveClientId(row);
        if (!id) return;

        const previousRows = rows;
        setUpdatingIds((prev) => [...prev, id]);
        setRows((prev) => prev.map((item) =>
            getComparisonBoardActiveClientId(item) === id
                ? {
                    ...item,
                    enabled,
                    linkedAccount: {
                        ...(item.linkedAccount || {}),
                        active: enabled,
                    },
                }
                : item));

        try {
            await updateComparisonBoardStatus({ clientId: id, active: enabled }).unwrap();
        } catch (error) {
            console.error(error);
            setRows(previousRows);
        } finally {
            setUpdatingIds((prev) => prev.filter((item) => item !== id));
        }
    };

    return (
        <div className='w-full'>
            <div className='flex flex-col gap-3 md:flex-row md:items-start md:justify-between'>
                <div>
                    <h1 className='text-xl md:text-2xl font-bold text-[#101010] dark:text-primary-darkMode'>
                        Leo Comparison Boards
                    </h1>

                    <p className='mt-2 text-sm text-gray-600 dark:text-gray-400'>
                        Manage client comparison boards and property website URLs.
                    </p>
                </div>

                <button
                    type='button'
                    onClick={() => {
                        setCreateError(null);
                        setCreateModalOpen(true);
                    }}
                    className='inline-flex items-center justify-center rounded-xl border border-[#00330f] bg-[#00330f] px-4 py-2 text-sm font-semibold text-white dark:border-white dark:bg-white dark:text-black hover:opacity-90'
                >
                    Add Comparison Board
                </button>
            </div>

            <ComparisonBoardFilters
                totalElements={pageData?.totalElements || pageData?.total || rows.length || 0}
                filters={filters}
                loading={loading}
                onChange={setFilters}
                onClear={onClearFilters}
            />

            <ComparisonBoardsTable
                rows={rows}
                loading={loading}
                loadingMore={loadingMore}
                hasMore={hasMore}
                updatingIds={updatingIds}
                scrollContainerRef={scrollContainerRef}
                onLoadMore={onLoadMore}
                onChangeStatus={onChangeStatus}
            />

            <CreateComparisonBoardModal
                open={createModalOpen}
                loading={creatingBoard}
                loadingClients={loadingClients}
                loadingProperties={loadingProperties}
                error={createError}
                clients={clients}
                properties={properties}
                onClose={() => {
                    setCreateError(null);
                    setCreateModalOpen(false);
                }}
                onSubmit={onCreateBoard}
            />
        </div>
    );
};

export default LeoComparisonBoards;
